<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useActivityFeed } from '@/composables/cockpit/useActivityFeed'
import type { ActivityType, CockpitActivityItem } from '@/types/cockpit'

const { t, locale } = useI18n()
const router = useRouter()

const {
  items,
  loading,
  error,
  typeFilter,
  lastUpdatedAt,
  hasMore,
  fetchLatest,
  loadOlder,
  setFilter,
  startPolling,
  stopPolling,
  pause,
  resume,
} = useActivityFeed()

// Respect prefers-reduced-motion for slide-in animations
const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Time since last update display
const secondsSinceUpdate = ref<number | null>(null)

let secondsTimer: ReturnType<typeof setInterval> | null = null

function startSecondsTimer(): void {
  secondsTimer = setInterval(() => {
    if (!lastUpdatedAt.value) {
      secondsSinceUpdate.value = null
      return
    }
    secondsSinceUpdate.value = Math.floor((Date.now() - lastUpdatedAt.value.getTime()) / 1000)
  }, 1000)
}

const updatedAgoText = computed(() => {
  const secs = secondsSinceUpdate.value
  if (secs === null) return t('admin.cockpit.activity.live_connecting')
  if (secs < 60) return t('admin.cockpit.activity.live_updated_seconds', { secs })
  const mins = Math.floor(secs / 60)
  return t('admin.cockpit.activity.live_updated_minutes', { mins })
})

// Activity type filter options
const filterOptions: Array<{ key: ActivityType | null; label: string }> = [
  { key: null, label: 'admin.cockpit.activity.filter_all' },
  { key: 'payment_received', label: 'admin.cockpit.activity.filter_payments' },
  { key: 'registration_created', label: 'admin.cockpit.activity.filter_registrations' },
  { key: 'organisation_created', label: 'admin.cockpit.activity.filter_orgs' },
  { key: 'registration_refunded', label: 'admin.cockpit.activity.filter_refunds' },
  { key: 'user_registered', label: 'admin.cockpit.activity.filter_users' },
]

// Compose localised sentence from structured activity fields
function composeSentence(item: CockpitActivityItem): string {
  const summary = item.summary
  const orgName = item.organisation?.name ?? ''
  const amount = item.amount
    ? new Intl.NumberFormat(locale.value, {
        style: 'currency',
        currency: (item.currency ?? 'eur').toUpperCase(),
      }).format(parseFloat(item.amount))
    : ''

  switch (item.type) {
    case 'payment_received':
      return t('admin.cockpit.activity.sentence.payment_received', {
        amount,
        event: String(summary.event_name ?? ''),
        org: orgName,
      })
    case 'registration_created':
      return t('admin.cockpit.activity.sentence.registration_created', {
        participant: String(summary.participant_name ?? ''),
        event: String(summary.event_name ?? ''),
        org: orgName,
      })
    case 'registration_refunded':
      return t('admin.cockpit.activity.sentence.registration_refunded', {
        amount,
        event: String(summary.event_name ?? ''),
        org: orgName,
      })
    case 'organisation_created':
      return t('admin.cockpit.activity.sentence.organisation_created', { org: orgName })
    case 'stripe_connected':
      return t('admin.cockpit.activity.sentence.stripe_connected', { org: orgName })
    case 'edition_created':
      return t('admin.cockpit.activity.sentence.edition_created', {
        edition: String(summary.edition_name ?? ''),
        event: String(summary.event_name ?? ''),
        org: orgName,
      })
    case 'user_registered':
      return t('admin.cockpit.activity.sentence.user_registered', {
        name: String(summary.user_name ?? ''),
      })
    default:
      return item.type
  }
}

function activityIcon(type: ActivityType): string {
  switch (type) {
    case 'payment_received': return '💶'
    case 'registration_created': return '🏃'
    case 'registration_refunded': return '↩'
    case 'organisation_created': return '🏢'
    case 'stripe_connected': return '🔗'
    case 'edition_created': return '📅'
    case 'user_registered': return '🎟'
    default: return '•'
  }
}

function activityTone(type: ActivityType): 'emerald' | 'red' | 'neutral' {
  if (type === 'payment_received') return 'emerald'
  if (type === 'registration_refunded') return 'red'
  return 'neutral'
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const secs = Math.floor(diff / 1000)
  if (secs < 60) return t('admin.cockpit.activity.time_just_now')
  const mins = Math.floor(secs / 60)
  if (mins < 60) return t('admin.cockpit.activity.time_minutes_ago', { mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('admin.cockpit.activity.time_hours_ago', { hours })
  const days = Math.floor(hours / 24)
  return t('admin.cockpit.activity.time_days_ago', { days })
}

function handleOrgClick(orgId: number | null | undefined): void {
  if (!orgId) return
  void router.push({ name: 'admin.organisations.show', params: { id: orgId } })
}

onMounted(() => {
  void fetchLatest()
  startPolling()
  startSecondsTimer()
})

onUnmounted(() => {
  stopPolling()
  if (secondsTimer !== null) {
    clearInterval(secondsTimer)
  }
})
</script>

<template>
  <div
    class="bg-slate-800 border border-slate-700 rounded-2xl p-6"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <!-- Header -->
    <div class="flex flex-wrap items-center justify-between gap-4 mb-5">
      <h2 class="text-lg font-bold text-white">{{ t('admin.cockpit.activity.title') }}</h2>

      <!-- Live indicator pill -->
      <div class="flex items-center gap-2 text-xs text-slate-400">
        <span class="flex items-center gap-1.5">
          <span class="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
          <span class="text-emerald-400 font-medium">{{ t('admin.cockpit.activity.live') }}</span>
        </span>
        <span>&middot;</span>
        <span>{{ updatedAgoText }}</span>
      </div>
    </div>

    <!-- Type filter -->
    <div class="flex flex-wrap gap-1.5 mb-5">
      <button
        v-for="option in filterOptions"
        :key="option.key ?? 'all'"
        type="button"
        class="px-3 py-1 text-xs font-medium rounded-full transition-colors border"
        :class="
          typeFilter === option.key
            ? 'bg-emerald-600 border-emerald-600 text-white'
            : 'bg-transparent border-slate-600 text-slate-400 hover:border-slate-500 hover:text-white'
        "
        @click="setFilter(option.key)"
      >
        {{ t(option.label) }}
      </button>
    </div>

    <!-- Error: refresh-failed, but keeping stale items -->
    <div
      v-if="error === 'admin.cockpit.activity.error_refresh' && items.length > 0"
      class="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center justify-between mb-4 text-sm"
    >
      <p class="text-red-300">{{ t('admin.cockpit.activity.error_refresh_msg') }}</p>
      <button
        type="button"
        class="text-red-300 hover:text-red-200 font-medium underline text-xs"
        @click="fetchLatest"
      >
        {{ t('admin.common.retry') }}
      </button>
    </div>

    <!-- Loading: skeleton rows -->
    <div
      v-if="loading && items.length === 0"
      class="space-y-3"
      aria-label="Loading activity feed"
    >
      <div
        v-for="i in 5"
        :key="i"
        class="flex items-start gap-3 animate-pulse"
      >
        <div class="w-8 h-8 bg-slate-700 rounded-full shrink-0" />
        <div class="flex-1 space-y-2">
          <div class="h-3 bg-slate-700 rounded w-3/4" />
          <div class="h-3 bg-slate-700 rounded w-1/3" />
        </div>
      </div>
    </div>

    <!-- Fatal error (no items yet) -->
    <div
      v-else-if="error && items.length === 0"
      class="bg-red-500/10 border border-red-500/30 rounded-xl p-5 flex items-center justify-between"
    >
      <p class="text-red-300 text-sm">{{ t('admin.cockpit.activity.error') }}</p>
      <button
        type="button"
        class="text-sm text-red-300 hover:text-red-200 font-medium underline"
        @click="fetchLatest"
      >
        {{ t('admin.common.retry') }}
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!loading && items.length === 0"
      class="text-center text-slate-500 py-10 text-sm"
    >
      {{ t('admin.cockpit.activity.empty') }}
    </div>

    <!-- Activity list -->
    <div
      v-else
      aria-live="polite"
      aria-label="Recent platform activity"
      class="space-y-1"
    >
      <div
        v-for="item in items"
        :key="item.id"
        class="flex items-start gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-700/50 transition-colors group"
        :class="{ 'animate-slide-in': !prefersReducedMotion }"
      >
        <!-- Icon -->
        <span class="text-lg shrink-0 w-7 text-center" aria-hidden="true">
          {{ activityIcon(item.type) }}
        </span>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <p
            class="text-sm leading-snug"
            :class="{
              'text-emerald-300': activityTone(item.type) === 'emerald',
              'text-red-300': activityTone(item.type) === 'red',
              'text-white': activityTone(item.type) === 'neutral',
            }"
          >
            {{ composeSentence(item) }}
          </p>
          <!-- Org link -->
          <button
            v-if="item.organisation?.id"
            type="button"
            class="text-xs text-slate-400 hover:text-emerald-400 transition-colors mt-0.5"
            @click="handleOrgClick(item.organisation.id)"
          >
            {{ item.organisation.name }}
          </button>
        </div>

        <!-- Relative timestamp -->
        <span class="text-xs text-slate-500 shrink-0 mt-0.5">
          {{ relativeTime(item.occurred_at) }}
        </span>
      </div>
    </div>

    <!-- Load older -->
    <div v-if="hasMore && items.length > 0" class="mt-4 text-center">
      <button
        type="button"
        class="text-sm text-slate-400 hover:text-white font-medium transition-colors disabled:opacity-50"
        :disabled="loading"
        @click="loadOlder"
      >
        {{ loading ? t('admin.common.loading') : t('admin.cockpit.activity.load_older') }}
      </button>
    </div>
  </div>
</template>
